/**
 * Dosing entry form - extracted from ManualDosingModal for reuse.
 *
 * Features:
 * - Chemical name combobox with autocomplete from existing chemicals
 * - Volume input (mL)
 * - Date/time picker with future date validation
 * - Brand combobox with common brands + custom entry
 * - Notes textarea
 * - Error handling with dismissible alert
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Stack,
  Alert,
  NumberInput,
  Textarea,
  Button,
  Combobox,
  InputBase,
  useCombobox,
  ActionIcon,
  Loader,
  rem,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
  IconAlertCircle,
  IconCheck,
  IconX,
  IconDroplet,
  IconClock,
  IconNotes,
} from '@tabler/icons-react';
import { useDosingChemicals, useCreateManualDosing } from '@/hooks/useDosingEvents';
import type { ManualDosingRequest } from '@/types/dosing';

// Category display names for grouped dropdown
const CATEGORY_LABELS: Record<string, string> = {
  combo: 'Combo Products',
  major: 'Major Elements',
  macro: 'Macro Elements',
  trace: 'Trace Elements',
  nutrient_add: 'Nutrient Addition',
  nutrient_remove: 'Nutrient Removal',
  other: 'Other',
};

// Category sort order
const CATEGORY_ORDER = ['combo', 'major', 'macro', 'trace', 'nutrient_add', 'nutrient_remove', 'other'];

// Common brands for autocomplete (alphabetized)
const COMMON_BRANDS = [
  'Aquaforest',
  'ATI',
  'Brightwell Aquatics',
  'BRS',
  'DIY',
  'ESV',
  'Fauna Marin',
  'Red Sea',
  'Reef Zlements',
  'Seachem',
  'Tropic Marin',
  'Two Little Fishies',
];

export interface DosingFormProps {
  /** Callback when entry is successfully saved */
  onSuccess: (chemicalName: string, volumeMl: number, brand?: string) => void;
  /** Optional callback for error handling */
  onError?: (error: Error) => void;
}

/**
 * Brand selection combobox (Mantine v8 - Select creatable was removed)
 * Allows selecting from common brands or typing a custom brand.
 */
interface BrandComboboxProps {
  value: string;
  onChange: (value: string) => void;
}

function BrandCombobox({ value, onChange }: BrandComboboxProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const filteredOptions = useMemo(() => {
    const search = value.toLowerCase().trim();
    if (!search) return COMMON_BRANDS;
    return COMMON_BRANDS.filter((brand) =>
      brand.toLowerCase().includes(search)
    );
  }, [value]);

  const showCreateOption =
    value.trim() &&
    !COMMON_BRANDS.some((b) => b.toLowerCase() === value.toLowerCase().trim());

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          label="Brand (optional)"
          placeholder="Select or type brand"
          value={value}
          onChange={(e) => {
            onChange(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={
            value ? (
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          styles={{
            input: {
              fontSize: rem(16),
            },
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filteredOptions.map((brand) => (
            <Combobox.Option key={brand} value={brand}>
              {brand}
            </Combobox.Option>
          ))}
          {showCreateOption && (
            <Combobox.Option value={value.trim()}>
              + Add "{value.trim()}"
            </Combobox.Option>
          )}
          {filteredOptions.length === 0 && !showCreateOption && (
            <Combobox.Empty>No brands found</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export function DosingForm({ onSuccess, onError }: DosingFormProps) {
  // Form state
  const [chemicalName, setChemicalName] = useState('');
  const [volumeMl, setVolumeMl] = useState<number | string>(0);
  const [recordedAt, setRecordedAt] = useState<Date>(new Date());
  const [brand, setBrand] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Track if component is mounted to avoid state updates after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Combobox for chemical selection
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Fetch chemicals for autocomplete
  const { data: chemicalsData, isLoading: chemicalsLoading } = useDosingChemicals();
  const createMutation = useCreateManualDosing();

  // Prepare chemical options
  const chemicalOptions = useMemo(() => {
    if (!chemicalsData?.chemicals) return [];
    return chemicalsData.chemicals.map((c) => ({
      value: c.name,
      label: c.name,
      category: c.category,
    }));
  }, [chemicalsData]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    const search = chemicalName.toLowerCase().trim();
    if (!search) return chemicalOptions;
    return chemicalOptions.filter((opt) =>
      opt.label.toLowerCase().includes(search)
    );
  }, [chemicalOptions, chemicalName]);

  // Group filtered options by category
  const groupedOptions = useMemo(() => {
    const groups: Record<string, typeof filteredOptions> = {};

    // Initialize groups in order
    for (const category of CATEGORY_ORDER) {
      groups[category] = [];
    }

    // Assign options to groups
    for (const opt of filteredOptions) {
      const cat = opt.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(opt);
    }

    // Return only non-empty groups in order
    return CATEGORY_ORDER
      .filter((cat) => (groups[cat]?.length ?? 0) > 0)
      .map((cat) => ({
        category: cat,
        label: CATEGORY_LABELS[cat] || cat,
        options: groups[cat] ?? [],
      }));
  }, [filteredOptions]);

  // Reset form
  const resetForm = () => {
    setChemicalName('');
    setVolumeMl(0);
    setRecordedAt(new Date());
    setBrand('');
    setNotes('');
    setError(null);
  };

  // Handle submit
  const handleSubmit = async () => {
    setError(null);

    // Validation: required chemical name
    if (!chemicalName.trim()) {
      setError('Chemical name is required');
      return;
    }

    // Validation: volume > 0
    const volume = typeof volumeMl === 'string' ? parseFloat(volumeMl) : volumeMl;
    if (!volume || volume <= 0) {
      setError('Volume must be greater than 0');
      return;
    }

    // Validation: valid date (not future, with 5 min buffer)
    const now = new Date();
    const maxAllowed = new Date(now.getTime() + 5 * 60 * 1000);
    if (recordedAt > maxAllowed) {
      setError('Date cannot be in the future');
      return;
    }

    const request: ManualDosingRequest = {
      chemical_name: chemicalName.trim(),
      volume_ml: volume,
      recorded_at: recordedAt.toISOString(),
      brand: brand.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    try {
      await createMutation.mutateAsync(request);
      if (isMountedRef.current) {
        onSuccess(chemicalName.trim(), volume, brand.trim() || undefined);
        resetForm();
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to create entry');
      if (isMountedRef.current) {
        setError(errorObj.message);
      }
      onError?.(errorObj);
    }
  };

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          onClose={() => setError(null)}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      {/* Chemical Name - Combobox with autocomplete */}
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => {
          setChemicalName(val);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            label="Chemical"
            placeholder="Select or type chemical name"
            required
            value={chemicalName}
            onChange={(e) => {
              setChemicalName(e.currentTarget.value);
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            leftSection={<IconDroplet size={16} />}
            rightSection={chemicalsLoading ? <Loader size={16} /> : null}
            styles={{
              input: {
                fontSize: rem(16),
              },
            }}
          />
        </Combobox.Target>

        <Combobox.Dropdown mah={300} style={{ overflowY: 'auto' }}>
          <Combobox.Options>
            {groupedOptions.length > 0 ? (
              groupedOptions.map((group) => (
                <Combobox.Group key={group.category} label={group.label}>
                  {group.options.map((opt) => (
                    <Combobox.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Combobox.Option>
                  ))}
                </Combobox.Group>
              ))
            ) : (
              <Combobox.Empty>
                {chemicalName.trim()
                  ? `Press Enter to use "${chemicalName}"`
                  : 'No chemicals found'}
              </Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      {/* Volume */}
      <NumberInput
        label="Volume (mL)"
        placeholder="60"
        required
        min={0.1}
        max={10000}
        decimalScale={1}
        step={5}
        value={volumeMl}
        onChange={setVolumeMl}
        styles={{
          input: {
            fontSize: rem(16),
          },
        }}
      />

      {/* Date/Time */}
      <DateTimePicker
        label="Date & Time"
        required
        value={recordedAt}
        onChange={(val) => {
          if (val) {
            setRecordedAt(new Date(val));
          }
        }}
        leftSection={<IconClock size={16} />}
        maxDate={new Date()}
        clearable={false}
        valueFormat="MMM D, YYYY h:mm A"
        styles={{
          input: {
            fontSize: rem(16),
          },
        }}
      />

      {/* Brand - Combobox with autocomplete */}
      <BrandCombobox value={brand} onChange={setBrand} />

      {/* Notes */}
      <Textarea
        label="Notes (optional)"
        placeholder="Any additional notes..."
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        leftSection={<IconNotes size={16} />}
        maxLength={500}
        autosize
        minRows={2}
        maxRows={4}
        styles={{
          input: {
            fontSize: rem(16),
          },
        }}
      />

      {/* Submit Button */}
      <Button
        fullWidth
        size="md"
        onClick={handleSubmit}
        loading={createMutation.isPending}
        disabled={!chemicalName.trim() || volumeMl === 0 || volumeMl === ''}
        leftSection={<IconCheck size={18} />}
      >
        Log Dosing
      </Button>
    </Stack>
  );
}
